"use client";

import * as Accordion from "@radix-ui/react-accordion";
const styles = new Proxy({"VideoAccordion":"VideoAccordion-40e63","Title":"Title-40e63","CtaWrapper":"CtaWrapper-40e63","CtaButton":"CtaButton-40e63","Inner":"Inner-40e63","VideoCol":"VideoCol-40e63","VideoWrapper":"VideoWrapper-40e63","AccordionCol":"AccordionCol-40e63","Item":"Item-40e63","Trigger":"Trigger-40e63","StepNumber":"StepNumber-40e63","Content":"Content-40e63"}, { get: (m, k) => (typeof k === "string" ? (m[k] ?? k) : m[k]) });
import Image from "next/image";
import { vi } from "payload/i18n/vi";

type Step = {
  id: string;
  title: string;
  content: string | null;
};

type VideoAccordionProps = {
  block: {
    title?: string;
    steps?: Step[];
    videoUrl?: string;
    videoTitle?: string;
    defaultOpenId?: string;
    ctaLabel?: string;
    ctaUrl?: string;
  };
};

const DEFAULT_STEPS: Step[] = [
  {
    id: "1",
    title: "3D Body Modelling",
    content:
      "Our system compares anthropometric features extracted from archived footage " +
      "with those detected in live video or crime scene recordings, calculating a " +
      "biometric match probability score based on over 100 objective metrics.",
  },
  {
    id: "2",
    title: "Gait & Body Analysis",
    content:
      "Our system compares anthropometric features extracted from archived footage " +
      "with those detected in live video or crime scene recordings, calculating a " +
      "biometric match probability score based on over 100 objective metrics.",
  },
  {
    id: "3",
    title: "Biometric Identification",
    content:
      "Our system compares anthropometric features extracted from archived footage " +
      "with those detected in live video or crime scene recordings, calculating a " +
      "biometric match probability score based on over 100 objective metrics.",
  },
];

const DEFAULT_VIDEO_URL =
  "https://player.vimeo.com/video/1170967718?badge=0&autopause=0&player_id=0&app_id=58479";

const DEFAULT_VIDEO_TITLE = "Gait & Body Recognition by Cursor Insight";

const DEFAULT_TITLE = "How Gait & Body Recognition Works";
const DEFAULT_CTA_LABEL = "Book a demo";
const DEFAULT_CTA_URL =
  "https://calendly.com/tamas-cursor-insight/consultation";

export default function VideoAccordion({ block }: VideoAccordionProps) {
  const steps = block.steps?.length ? block.steps : DEFAULT_STEPS;
  const videoUrl = block.videoUrl?.length ? block.videoUrl : DEFAULT_VIDEO_URL;
  const videoTitle = block.videoTitle ?? DEFAULT_VIDEO_TITLE;
  const defaultOpenId = block.defaultOpenId ?? steps[0]?.id;
  const title = block.title ?? DEFAULT_TITLE;
  const ctaLabel = block.ctaLabel ?? DEFAULT_CTA_LABEL;
  const ctaUrl = block.ctaUrl ?? DEFAULT_CTA_URL;

  const url = new URL(videoUrl);
  const isVimeo = url.hostname.includes("vimeo.com");
  const isYouTube =
    url.hostname.includes("youtube.com") || url.hostname.includes("youtu.be");
  const isVideo = url.pathname.endsWith("mp4") || url.pathname.endsWith("webm");
  const isImage =
    url.pathname.endsWith("png") ||
    url.pathname.endsWith("jpg") ||
    url.pathname.endsWith("jpeg");

  return (
    <section className={styles.VideoAccordion}>
      {title && <h2 className={styles.Title}>{title}</h2>}
      <div className={styles.Inner}>
        <div className={styles.VideoCol}>
          <div className={styles.VideoWrapper}>
            {isImage ? (
              <Image src={videoUrl} alt={videoTitle} width={500} height={360} />
            ) : isVideo ? (
              <video controls>
                <source
                  src={videoUrl}
                  type={`video/${url.pathname.split(".").pop()}`}
                />
                Your browser does not support the video tag.
              </video>
            ) : isVimeo ? (
              <iframe
                width="100%"
                src={`https://player.vimeo.com/video/${videoUrl?.match(/vimeo\.com\/(?:video\/)?([^?/&]+)/)?.[1]}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                title={videoTitle}
                style={{ aspectRatio: "16/9" }}
                tabIndex={-1}
              />
            ) : isYouTube ? (
              <iframe
                width="100%"
                src={`https://www.youtube-nocookie.com/embed/${videoUrl?.match(/(?:(?:m\.)?youtube(?:-nocookie)?\.com\/(?:embed\/|watch\?(?:.*&)?v=)|youtu\.be\/)([^?/&]+)/)?.[1]}?modestbranding=1&rel=0&hl=es`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                title={videoTitle}
                style={{ aspectRatio: "16/9" }}
                tabIndex={-1}
              />
            ) : (
              "Unsupported media type"
            )}
          </div>
        </div>

        <Accordion.Root
          type="single"
          defaultValue={defaultOpenId}
          className={styles.AccordionCol}
        >
          {steps.map((step) => (
            <Accordion.Item
              key={step.id}
              value={step.id}
              className={styles.Item}
            >
              <Accordion.Header>
                <Accordion.Trigger className={styles.Trigger}>
                  <span className={styles.StepNumber}>{step.id}</span>
                  {step.title}
                </Accordion.Trigger>
              </Accordion.Header>
              {step.content && (
                <Accordion.Content className={styles.Content}>
                  {step.content}
                </Accordion.Content>
              )}
            </Accordion.Item>
          ))}
        </Accordion.Root>
      </div>

      <div className={styles.CtaWrapper}>
        <a
          href={ctaUrl}
          className={styles.CtaButton}
          target="_blank"
          rel="noopener noreferrer"
        >
          {ctaLabel}
        </a>
      </div>
    </section>
  );
}
